// frontend/src/pages/OwnerSettings.js
import { useEffect, useState, useMemo } from "react";
import { BACKEND_URL } from "../config";
import { safeGetItem } from "../utils/storage";

export default function OwnerSettings() {
  const [consultancy, setConsultancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = safeGetItem("token");
  const mediaBaseUrl = useMemo(
    () => BACKEND_URL.replace(/\/api$/, ""),
    []
  );

  // Text inputs for nicer typing (commas etc.)
  const [countries, setCountries] = useState([]);
  const [courses, setCourses] = useState([]);
  const [countryInput, setCountryInput] = useState("");
  const [courseInput, setCourseInput] = useState("");

  // NEW: structured address fields
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [countryName, setCountryName] = useState("");
  const [stateName, setStateName] = useState("");

  const [unsaved, setUnsaved] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle, saving, success, error
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [bannerPreview, setBannerPreview] = useState("");

  // 🔹 LOAD OWNER CONSULTANCY
  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${BACKEND_URL}/consultancies/owner/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();

        // Normalise address
        const resolvedAddress =
          data.location ||
          data.address ||
          (data.contact && data.contact.address) ||
          "";

        // Normalise arrays
        const normalisedCountries = Array.isArray(data.countries)
          ? data.countries
          : typeof data.countries === "string" && data.countries.trim()
          ? data.countries.split(",").map((s) => s.trim()).filter(Boolean)
          : [];

        const normalisedCourses = Array.isArray(data.courses)
          ? data.courses
          : typeof data.courses === "string" && data.courses.trim()
          ? data.courses.split(",").map((s) => s.trim()).filter(Boolean)
          : [];

        const preparedConsultancy = {
          ...data,
          location: resolvedAddress,
          address: resolvedAddress,
          countries: normalisedCountries,
          courses: normalisedCourses,
          gallery: data.gallery || [],
        };

        let derivedLine1 = "";
        let derivedLine2 = "";
        let derivedCity = "";
        let derivedPin = "";
        let derivedState = data.state || "";
        let derivedCountry = data.country || "";

        if (resolvedAddress) {
          const parts = resolvedAddress
            .split(",")
            .map((p) => p.trim())
            .filter(Boolean);

          if (parts[0]) derivedLine1 = parts[0];
          if (parts[1]) derivedLine2 = parts[1];

          // last part might contain city / pincode
          if (parts.length >= 3) {
            const last = parts[parts.length - 1];
            const pinMatch = last.match(/(\d{5,6})/); // 5 or 6 digits
            if (pinMatch) {
              derivedPin = pinMatch[1];
              derivedCity = last.replace(pinMatch[1], "").replace(/[-,]/g, "").trim();
            } else {
              derivedCity = last;
            }
          }

          if (!derivedState && parts.length >= 4) {
            derivedState = parts[parts.length - 2];
          }
          if (!derivedCountry && parts.length >= 5) {
            derivedCountry = parts[parts.length - 3];
          }
        }

        setConsultancy(preparedConsultancy);
        setCountries(normalisedCountries);
        setCourses(normalisedCourses);
        setAddressLine1(derivedLine1);
        setAddressLine2(derivedLine2);
        setCity(derivedCity);
        setPincode(derivedPin);
        setStateName(derivedState);
        setCountryName(derivedCountry);

        setInitialSnapshot({
          consultancy: preparedConsultancy,
          addressLine1: derivedLine1,
          addressLine2: derivedLine2,
          city: derivedCity,
          pincode: derivedPin,
          stateName: derivedState,
          countryName: derivedCountry,
          countries: normalisedCountries,
          courses: normalisedCourses,
        });
        setUnsaved(false);
        setSaveState("idle");
      } catch (e) {
        console.error("Error loading owner consultancy", e);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [token]);

  useEffect(() => {
    return () => {
      if (bannerPreview) {
        URL.revokeObjectURL(bannerPreview);
      }
    };
  }, [bannerPreview]);

  const markUnsaved = () => {
    if (!unsaved) setUnsaved(true);
    if (saveState !== "idle") setSaveState("idle");
  };

  const updateField = (field, value) => {
    setConsultancy((prev) => {
      if (!prev) return prev;

      const updated = { ...prev, [field]: value };

      // keep location & address in sync if ever used
      if (field === "location") {
        updated.address = value;
      } else if (field === "address") {
        updated.location = value;
      }

      return updated;
    });
    markUnsaved();
  };

  // 🔹 SAVE CHANGES
  const saveChanges = async () => {
    const newErrors = {};
    if (!consultancy.name?.trim()) newErrors.name = "Consultancy name is required.";
    if (!consultancy.email?.trim()) newErrors.email = "Email is required.";
    if (!addressLine1.trim()) newErrors.addressLine1 = "Address line 1 is required.";
    if (!city.trim()) newErrors.city = "City is required.";
    if (!pincode.trim()) newErrors.pincode = "Postal code is required.";

    if (Object.keys(newErrors).length) {
      setFieldErrors(newErrors);
      return;
    }

    setFieldErrors({});

    // Combine structured fields into single location string
    const parts = [addressLine1.trim()];
    if (addressLine2.trim()) parts.push(addressLine2.trim());
    const cityLine = [city.trim(), stateName.trim()].filter(Boolean).join(", ");
    parts.push(cityLine ? `${cityLine} - ${pincode.trim()}` : `${city.trim()} - ${pincode.trim()}`);
    if (countryName.trim()) parts.push(countryName.trim());

    const locationValue = parts.join(", ");

    const body = {
      ...consultancy,
      countries,
      courses,
      location: locationValue,
      address: locationValue,
      state: stateName,
      country: countryName,
    };

    setSaveState("saving");

    try {
      const res = await fetch(`${BACKEND_URL}/consultancies/owner/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        setConsultancy((prev) =>
          prev
            ? {
                ...prev,
                ...body,
              }
            : prev
        );
        const snapshot = {
          consultancy: body,
          addressLine1,
          addressLine2,
          city,
          pincode,
          stateName,
          countryName,
          countries,
          courses,
        };
        setInitialSnapshot(snapshot);
        setUnsaved(false);
        setSaveState("success");
        setTimeout(() => setSaveState("idle"), 2500);
      } else {
        setSaveState("error");
        alert("Update failed.");
      }
    } catch (e) {
      console.error("Error saving consultancy", e);
      setSaveState("error");
      alert("Update failed.");
    }
  };

  // 🔹 LOGO UPLOAD
  const handleLogoUpload = async (file) => {
    if (!file) return;

    const fd = new FormData();
    fd.append("logo", file);

    try {
      const res = await fetch(
        `${BACKEND_URL}/consultancies/owner/upload-logo`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        }
      );

      const j = await res.json();
      if (res.ok && j.url) {
        setConsultancy((prev) =>
          prev ? { ...prev, logo: j.url } : prev
        );
        setInitialSnapshot((prev) =>
          prev
            ? {
                ...prev,
                consultancy: prev.consultancy
                  ? { ...prev.consultancy, logo: j.url }
                  : prev.consultancy,
              }
            : prev
        );
        setUnsaved(false);
        alert("Logo uploaded successfully!");
      } else {
        alert(j.error || "Upload failed");
      }
    } catch (e) {
      console.error("Error uploading logo", e);
      alert("Upload failed");
    }
  };

  // 🔹 GALLERY UPLOAD
  const handleGalleryUpload = async (files) => {
    if (!files || files.length === 0) return;

    const fd = new FormData();
    for (const f of files) fd.append("gallery", f);

    try {
      const res = await fetch(
        `${BACKEND_URL}/consultancies/owner/upload-gallery`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        }
      );

      const j = await res.json();
      if (res.ok && j.urls) {
        setConsultancy((prev) => ({
          ...prev,
          gallery: prev.gallery ? [...j.urls, ...prev.gallery] : j.urls,
        }));
        alert("Gallery uploaded successfully!");
      } else {
        alert(j.error || "Upload failed");
      }
    } catch (e) {
      console.error("Error uploading gallery", e);
      alert("Upload failed");
    }
  };

  const handleAddCountry = () => {
    const trimmed = countryInput.trim();
    if (!trimmed) return;
    if (!countries.includes(trimmed)) {
      setCountries((prev) => [...prev, trimmed]);
      markUnsaved();
    }
    setCountryInput("");
    setFieldErrors((prev) => ({ ...prev, countries: undefined }));
  };

  const handleRemoveCountry = (value) => {
    setCountries((prev) => prev.filter((c) => c !== value));
    markUnsaved();
  };

  const handleAddCourse = () => {
    const trimmed = courseInput.trim();
    if (!trimmed) return;
    if (!courses.includes(trimmed)) {
      setCourses((prev) => [...prev, trimmed]);
      markUnsaved();
    }
    setCourseInput("");
    setFieldErrors((prev) => ({ ...prev, courses: undefined }));
  };

  const handleRemoveCourse = (value) => {
    setCourses((prev) => prev.filter((c) => c !== value));
    markUnsaved();
  };

  const handleBannerSelect = (file) => {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setBannerPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return preview;
    });
    markUnsaved();
  };

  const handleReset = () => {
    if (!initialSnapshot) return;
    setConsultancy(initialSnapshot.consultancy);
    setCountries(initialSnapshot.countries || []);
    setCourses(initialSnapshot.courses || []);
    setAddressLine1(initialSnapshot.addressLine1 || "");
    setAddressLine2(initialSnapshot.addressLine2 || "");
    setCity(initialSnapshot.city || "");
    setPincode(initialSnapshot.pincode || "");
    setStateName(initialSnapshot.stateName || "");
    setCountryName(initialSnapshot.countryName || "");
    setCountryInput("");
    setCourseInput("");
    setBannerPreview("");
    setFieldErrors({});
    setUnsaved(false);
    setSaveState("idle");
  };

  const removeGallery = async (url) => {
    const ok = window.confirm("Remove this image?");
    if (!ok) return;

    try {
      const res = await fetch(
        `${BACKEND_URL}/consultancies/owner/remove-gallery`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ url }),
        }
      );

      if (res.ok) {
        setConsultancy((prev) => ({
          ...prev,
          gallery: (prev.gallery || []).filter((u) => u !== url),
        }));
      } else {
        alert("Could not remove image.");
      }
    } catch (e) {
      console.error("Error removing gallery image", e);
      alert("Could not remove image.");
    }
  };

  if (loading) return <div className="p-6 text-white">Loading...</div>;
  if (!consultancy) return <div className="p-6 text-white">Consultancy not found.</div>;

  const canEdit = true;
  const descriptionLimit = 600;
  const descriptionLength = consultancy.description?.length || 0;
  const displayDescriptionLength = Math.min(descriptionLength, descriptionLimit);

  const statusChip = { label: "● Free Beta Access", color: "text-sky-300" };

  return (
    <div className="min-h-screen bg-slate-950 pb-40 text-white">
      <div className="max-w-5xl mx-auto px-4 py-10 space-y-8">
        <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 shadow-[0_20px_60px_rgba(15,23,42,0.5)]">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm uppercase tracking-[6px] text-white/60">
                ⚙️ Consultancy Control Center
              </p>
              <h1 className="text-3xl font-semibold mt-2">
                Manage your brand, visibility & trust
              </h1>
              <p className="text-white/60 mt-1">
                Keep details up to date to build confidence with students.
              </p>
            </div>
            <div className={`px-4 py-2 rounded-full text-xs font-semibold tracking-[4px] border border-white/20 ${statusChip.color}`}>
              {statusChip.label}
            </div>
          </div>
          <div className="mt-4 rounded-2xl border border-cyan-400/30 bg-cyan-500/10 p-4 text-sm text-cyan-100">
            <strong className="block mb-1">Free Beta Mode</strong>
            All settings are unlocked while AbroadJacks is in open beta. Subscription plans will
            be introduced soon—nothing for you to do right now.
          </div>
          {unsaved && (
            <div className="mt-4 rounded-2xl border border-amber-400/30 bg-amber-500/10 p-4 text-sm text-amber-100 flex items-center gap-3">
              <span className="text-lg">⚠️</span>
              <div>You have unsaved changes</div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {/* Consultancy Info */}
          <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 shadow transition hover:-translate-y-1">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">🏢</span>
              <div>
                <h2 className="text-xl font-semibold">Consultancy Info</h2>
                <p className="text-sm text-white/60">Primary contact details</p>
              </div>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              {[
                { label: "Consultancy Name", value: consultancy.name || "", field: "name", type: "text", error: fieldErrors.name },
                { label: "Email Address", value: consultancy.email || "", field: "email", type: "email", error: fieldErrors.email },
                { label: "Phone Number", value: consultancy.phone || "", field: "phone", type: "text" },
                { label: "Website", value: consultancy.website || "", field: "website", type: "text" },
              ].map((input) => (
                <div key={input.field} className="flex flex-col gap-1">
                  <label className="text-sm text-white/70">{input.label}</label>
                  <input
                    type={input.type}
                    disabled={!canEdit}
                    value={input.value}
                    onChange={(e) => {
                      updateField(input.field, e.target.value);
                      setFieldErrors((prev) => ({ ...prev, [input.field]: undefined }));
                    }}
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/50 disabled:opacity-60"
                  />
                  {input.error && <p className="text-xs text-rose-300">{input.error}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Location Details */}
          <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 shadow transition hover:-translate-y-1">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">📌</span>
              <div>
                <h2 className="text-xl font-semibold">Location Details</h2>
                <p className="text-sm text-white/60">Help students find you easily</p>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {[
                { label: "Address Line 1", value: addressLine1, setter: setAddressLine1, key: "addressLine1", required: true },
                { label: "Address Line 2", value: addressLine2, setter: setAddressLine2, key: "addressLine2" },
                { label: "City", value: city, setter: setCity, key: "city", required: true },
                { label: "State / Province", value: stateName, setter: setStateName, key: "stateName" },
                { label: "Country", value: countryName, setter: setCountryName, key: "countryName" },
                { label: "Postal Code", value: pincode, setter: setPincode, key: "pincode", required: true },
              ].map((field) => (
                <div key={field.key} className="flex flex-col gap-1">
                  <label className="text-sm text-white/70">{field.label}</label>
                  <input
                    type="text"
                    disabled={!canEdit}
                    value={field.value}
                    onChange={(e) => {
                      field.setter(e.target.value);
                      setFieldErrors((prev) => ({ ...prev, [field.key]: undefined }));
                      markUnsaved();
                    }}
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/50 disabled:opacity-60"
                  />
                  {field.required && !field.value.trim() && fieldErrors[field.key] && (
                    <p className="text-xs text-rose-300">{fieldErrors[field.key]}</p>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-5">
              <div className="text-white/60 text-sm mb-2">Map Preview</div>
              <div className="h-52 rounded-3xl border border-white/10 bg-gradient-to-br from-blue-600/30 to-purple-600/30 flex items-center justify-center text-white/70 text-sm">
                Map preview coming soon
              </div>
            </div>
          </div>

          {/* Branding */}
          <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 shadow transition hover:-translate-y-1 space-y-5">
            <div className="flex items-center gap-3">
              <span className="text-3xl">🎨</span>
              <div>
                <h2 className="text-xl font-semibold">Branding</h2>
                <p className="text-sm text-white/60">Logo, cover and gallery</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-6 items-center">
              <div className="w-32 h-32 rounded-full bg-white/10 border border-white/20 flex items-center justify-center shadow-inner overflow-hidden">
                {consultancy.logo ? (
                  <img
                    src={
                      consultancy.logo.startsWith("http")
                        ? consultancy.logo
                        : `${mediaBaseUrl}${consultancy.logo}`
                    }
                    alt="Logo"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-white/60 text-sm">No Logo</span>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={!canEdit}
                    onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                  />
                  <label
                    htmlFor="logo-upload"
                    className={`inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-semibold bg-gradient-to-r from-blue-500 to-indigo-500 cursor-pointer ${
                      !canEdit ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    Upload New Logo
                  </label>
                </div>
                <div>
                  <input
                    id="banner-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={!canEdit}
                    onChange={(e) => handleBannerSelect(e.target.files?.[0])}
                  />
                  <label
                    htmlFor="banner-upload"
                    className={`inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-semibold bg-white/10 border border-white/20 cursor-pointer ${
                      !canEdit ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    Upload Banner Image
                  </label>
                </div>
              </div>
            </div>
            {bannerPreview && (
              <div className="rounded-3xl border border-white/10 overflow-hidden">
                <img
                  src={bannerPreview}
                  alt="Banner preview"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-48 object-cover"
                />
              </div>
            )}
            <div>
              <div className="text-sm text-white/60 mb-2">Gallery</div>
              <input
                type="file"
                accept="image/*"
                multiple
                disabled={!canEdit}
                className="text-sm mb-3 disabled:opacity-50"
                onChange={(e) => handleGalleryUpload(e.target.files)}
              />
              {consultancy.gallery?.length ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {(consultancy.gallery || []).map((img, idx) => (
                    <div key={idx} className="relative rounded-2xl overflow-hidden border border-white/10">
                      <img
                        src={img.startsWith("http") ? img : `${mediaBaseUrl}${img}`}
                        alt={`Gallery ${idx + 1}`}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-24 object-cover"
                      />
                      <button
                        disabled={!canEdit}
                        onClick={() => removeGallery(img)}
                        className="absolute top-2 right-2 bg-red-500/80 rounded-full text-xs px-2 py-1 disabled:opacity-60"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-white/60">No gallery images uploaded yet.</p>
              )}
            </div>
          </div>

          {/* About */}
          <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 shadow transition hover:-translate-y-1">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">📝</span>
              <div>
                <h2 className="text-xl font-semibold">About Consultancy</h2>
                <p className="text-sm text-white/60">
                  Tell students why they should choose you
                </p>
              </div>
            </div>
            <div className="relative">
              <textarea
                disabled={!canEdit}
                placeholder="Highlight success stories, expertise, and unique differentiators..."
                value={consultancy.description || ""}
                onChange={(e) => {
                  updateField("description", e.target.value.slice(0, descriptionLimit));
                }}
                className="w-full min-h-[160px] rounded-3xl border border-white/10 bg-white/5 px-5 py-4 text-sm focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/50 disabled:opacity-60"
              />
              <div className="absolute bottom-3 right-5 text-xs text-white/50">
                {displayDescriptionLength}/{descriptionLimit}
              </div>
            </div>
          </div>

          {/* Expertise */}
          <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 shadow transition hover:-translate-y-1 space-y-5">
            <div className="flex items-center gap-3">
              <span className="text-3xl">📚</span>
              <div>
                <h2 className="text-xl font-semibold">Areas of Expertise</h2>
                <p className="text-sm text-white/60">
                  Showcase countries and programs you specialize in
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold mb-2">Countries</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {countries.map((country) => (
                    <span
                      key={country}
                      className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-sm"
                    >
                      {country}
                      {canEdit && (
                        <button onClick={() => handleRemoveCountry(country)} className="text-xs text-rose-300">
                          ✕
                        </button>
                      )}
                    </span>
                  ))}
                  {countries.length === 0 && (
                    <span className="text-white/50 text-sm">No countries added yet</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    disabled={!canEdit}
                    value={countryInput}
                    onChange={(e) => setCountryInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCountry();
                      }
                    }}
                    placeholder="Add country and press Enter"
                    className="flex-1 rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/50 disabled:opacity-60"
                  />
                  <button
                    type="button"
                    disabled={!canEdit}
                    onClick={handleAddCountry}
                    className="px-4 py-2 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 text-sm font-semibold disabled:opacity-60"
                  >
                    Add
                  </button>
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold mb-2">Courses & Programs</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {courses.map((course) => (
                    <span
                      key={course}
                      className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-sm"
                    >
                      {course}
                      {canEdit && (
                        <button onClick={() => handleRemoveCourse(course)} className="text-xs text-rose-300">
                          ✕
                        </button>
                      )}
                    </span>
                  ))}
                  {courses.length === 0 && (
                    <span className="text-white/50 text-sm">No programs added yet</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    disabled={!canEdit}
                    value={courseInput}
                    onChange={(e) => setCourseInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCourse();
                      }
                    }}
                    placeholder="Add course/program"
                    className="flex-1 rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/50 disabled:opacity-60"
                  />
                  <button
                    type="button"
                    disabled={!canEdit}
                    onClick={handleAddCourse}
                    className="px-4 py-2 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 text-sm font-semibold disabled:opacity-60"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-4 sm:pb-6">
        <div className="mx-auto flex max-w-4xl flex-col gap-3 rounded-2xl border border-white/10 bg-slate-950/85 p-3 shadow-[0_20px_60px_rgba(15,23,42,0.7)] backdrop-blur sm:rounded-3xl sm:p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2 text-sm text-white/80">
            {saveState === "success" ? (
              <>
                <span>✅</span>
                <span>Saved!</span>
              </>
            ) : unsaved ? (
              <>
                <span>🕗</span>
                <span>You have unsaved changes</span>
              </>
            ) : (
              <>
                <span>💾</span>
                <span>All changes are up to date</span>
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="min-h-[44px] rounded-2xl border border-white/30 bg-white/5 px-5 py-3 text-sm font-semibold hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={!canEdit}
            >
              Reset
            </button>
            <button
              type="button"
              onClick={saveChanges}
              disabled={!canEdit || saveState === "saving"}
              className="min-h-[44px] rounded-2xl bg-gradient-to-r from-emerald-400 to-green-500 px-6 py-3 font-semibold text-slate-900 shadow-lg disabled:opacity-60"
            >
              {saveState === "saving" ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
